import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Flex,
  Icon,
  Badge,
  Card,
  CardBody,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  useColorMode,
  Divider,
} from "@chakra-ui/react";
import { CheckIcon, StarIcon } from "@chakra-ui/icons";
import { FaCoins, FaRocket, FaCrown, FaShieldAlt } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const PurchasePage = () => {
  const { colorMode } = useColorMode();

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? "rgba(255,255,255,0.95)"
      : "rgba(20,20,20,0.8)";
  const cardBorder =
    colorMode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)";
  const textPrimary = colorMode === "light" ? "gray.800" : "white";
  const textSecondary = colorMode === "light" ? "gray.600" : "gray.300";
  const textMuted = colorMode === "light" ? "gray.500" : "gray.500";

  const features = [
    "Full Business Builder Platform Access",
    "CRM, Projects, Billing, Calendar",
    "AI Voice Assistant (Vapi)",
    "Email & SMS Campaign Tools",
    "IPFS Decentralized File Storage",
    "Employee & Shift Management",
    "Custom Domain & Branding",
    "Accountability & Coaching Module",
    "Monthly BB Token Loyalty Rewards",
    "Priority Support from Tom",
  ];

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="1200px" py={16}>
        <VStack spacing={10}>
          {/* Hero */}
          <VStack spacing={4} textAlign="center">
            <Badge
              bg="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
              color="white"
              fontSize="lg"
              px={6}
              py={2}
              borderRadius="full"
              fontWeight="700"
            >
              <Icon as={StarIcon} mr={2} />
              Special Webinar Offer
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="800"
              color={textPrimary}
            >
              Ready to Take Control?
            </Heading>
            <Text fontSize="xl" color={textSecondary} maxW="700px">
              As a webinar attendee, you get exclusive pricing on Business Builder.
              Own your data. Train your AI. Scale your way.
            </Text>
          </VStack>

          {/* Pricing Cards */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="100%" maxW="900px">
            {/* Standard Plan */}
            <Card
              bg={cardGradientBg}
              borderWidth="2px"
              borderColor={cardBorder}
              borderRadius="2xl"
              boxShadow="0 8px 32px rgba(0,0,0,0.15)"
            >
              <CardBody p={8}>
                <VStack spacing={4} align="start">
                  <Badge colorScheme="purple" fontSize="sm">Foundation</Badge>
                  <Heading size="xl" color={textPrimary}>
                    $97<Text as="span" fontSize="lg" fontWeight="400" color={textSecondary}>/month</Text>
                  </Heading>
                  <Text color={textSecondary}>
                    Full platform access with all core modules.
                    Cancel anytime.
                  </Text>

                  <Divider />

                  <List spacing={2}>
                    {features.slice(0, 7).map((feature, i) => (
                      <ListItem key={i} fontSize="sm" color={textSecondary}>
                        <ListIcon as={CheckIcon} color="#10B981" />
                        {feature}
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    size="lg"
                    w="100%"
                    bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)"
                    color="white"
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(147, 51, 234, 0.5)",
                    }}
                    py={7}
                  >
                    Subscribe — $97/mo
                  </Button>

                  <Flex align="center" gap={2} w="100%" justify="center">
                    <Icon as={FaCoins} color="#F59E0B" />
                    <Text fontSize="sm" color={textSecondary}>
                      Or pay with 2 BB Tokens
                    </Text>
                  </Flex>
                </VStack>
              </CardBody>
            </Card>

            {/* Premium Setup */}
            <Card
              bg="linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)"
              borderWidth="3px"
              borderColor="rgba(245, 158, 11, 0.5)"
              borderRadius="2xl"
              boxShadow="0 12px 40px rgba(245, 158, 11, 0.25)"
              position="relative"
              overflow="hidden"
            >
              <Badge
                position="absolute"
                top={4}
                right={4}
                bg="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
                color="white"
                fontSize="xs"
                px={3}
                py={1}
                borderRadius="full"
              >
                BEST VALUE
              </Badge>
              <CardBody p={8}>
                <VStack spacing={4} align="start">
                  <Flex align="center" gap={2}>
                    <Icon as={FaCrown} color="#F59E0B" />
                    <Badge colorScheme="yellow" fontSize="sm">Premium Setup</Badge>
                  </Flex>
                  <Heading size="xl" color={textPrimary}>
                    $497
                    <Text as="span" fontSize="lg" fontWeight="400" color={textSecondary}> + $97/mo</Text>
                  </Heading>
                  <Text color={textSecondary}>
                    White-glove setup with Tom personally configuring your
                    platform. Everything included plus premium features.
                  </Text>

                  <Divider />

                  <List spacing={2}>
                    {features.map((feature, i) => (
                      <ListItem key={i} fontSize="sm" color={textSecondary}>
                        <ListIcon as={CheckIcon} color="#F59E0B" />
                        {feature}
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    size="lg"
                    w="100%"
                    bg="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
                    color="white"
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(245, 158, 11, 0.5)",
                    }}
                    py={7}
                  >
                    Get Premium Setup — $497
                  </Button>

                  <Text fontSize="xs" color={textMuted} textAlign="center" w="100%">
                    Includes 1-on-1 onboarding call with Tom
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Token payment info */}
          <Card
            bg="rgba(147, 51, 234, 0.1)"
            borderWidth="1px"
            borderColor="rgba(147, 51, 234, 0.3)"
            borderRadius="xl"
            maxW="700px"
            w="100%"
          >
            <CardBody>
              <VStack spacing={3}>
                <Flex align="center" gap={2}>
                  <Icon as={FaCoins} w={6} h={6} color="#F59E0B" />
                  <Heading size="md" color={textPrimary}>
                    Pay With BB Tokens
                  </Heading>
                </Flex>
                <Text color={textSecondary} textAlign="center">
                  Already earned BB Tokens? Use them! 2 tokens = 1 month free ($97 value).
                  Sign up as a subscriber and get an instant 2-token welcome bonus.
                </Text>
                <Button
                  variant="outline"
                  borderColor="#9333EA"
                  color="#9333EA"
                  _hover={{ bg: "rgba(147, 51, 234, 0.1)" }}
                  onClick={() => window.location.href = "/businessbuilder/rewards"}
                >
                  Check Your Token Balance
                </Button>
              </VStack>
            </CardBody>
          </Card>

          {/* Guarantee */}
          <Flex
            align="center"
            gap={3}
            bg="rgba(16, 185, 129, 0.1)"
            p={6}
            borderRadius="xl"
            borderWidth="1px"
            borderColor="rgba(16, 185, 129, 0.3)"
            maxW="700px"
            w="100%"
          >
            <Icon as={FaShieldAlt} w={8} h={8} color="#10B981" />
            <Box>
              <Text fontWeight="700" color={textPrimary}>
                30-Day Money-Back Guarantee
              </Text>
              <Text fontSize="sm" color={textSecondary}>
                Try Business Builder risk-free. If it's not for you, get a full refund within 30 days.
                No questions asked.
              </Text>
            </Box>
          </Flex>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PurchasePage;
